type UseOverflowItemsOptions<T> = {
  items: T[];
  containerWidth: number;
  getItemWidth: (item: T, index: number) => number;
  overflowTriggerWidth: number;
  gap?: number;
};

type UseOverflowItemsResult<T> = {
  visibleItems: T[];
  hiddenItems: T[];
};

/**
 * Splits an item list into `visibleItems` and `hiddenItems` based on how many
 * fit within a measured container width.
 *
 * Unlike `useHiddenItemsList` (which manages its own ResizeObserver and DOM
 * measurements), this hook is a pure computation — you supply pre-computed
 * widths and it returns the partition. This makes it a good fit for table cells
 * and other contexts where you already track element sizes via `useElementWidth`
 * or know them statically.
 *
 * @param options.items - The full list of items to partition.
 * @param options.containerWidth - Available width in pixels (typically from `useElementWidth`).
 * @param options.getItemWidth - Returns the width of a single item. Must be a stable reference
 *   (module-level function for constant widths, or `useCallback` for dynamic widths).
 * @param options.overflowTriggerWidth - Width reserved for the "+N" overflow trigger button.
 *   Only counted when items actually overflow; ignored when everything fits.
 * @param options.gap - Horizontal gap between items (default `0`).
 * @returns `{ visibleItems, hiddenItems }` — two slices of the original array.
 */
const useOverflowItems = <T>({
  items,
  containerWidth,
  getItemWidth,
  overflowTriggerWidth,
  gap = 0,
}: UseOverflowItemsOptions<T>): UseOverflowItemsResult<T> => {
  if (!items.length || !containerWidth) {
    return { visibleItems: items, hiddenItems: [] as T[] };
  }

  let totalWidth = 0;
  for (let i = 0; i < items.length; i++) {
    totalWidth += getItemWidth(items[i], i) + (i < items.length - 1 ? gap : 0);
  }

  if (totalWidth <= containerWidth) {
    return { visibleItems: items, hiddenItems: [] as T[] };
  }

  let usedWidth = overflowTriggerWidth;

  for (let i = 0; i < items.length; i++) {
    usedWidth += getItemWidth(items[i], i) + gap;

    if (usedWidth > containerWidth) {
      return {
        visibleItems: items.slice(0, i),
        hiddenItems: items.slice(i),
      };
    }
  }

  return { visibleItems: items, hiddenItems: [] as T[] };
};

export default useOverflowItems;
